import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Star, Loader2, MessageSquareHeart } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
    ))}
  </span>
);

export const AdminFeedbackTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/feedback`, { withCredentials: true });
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  return (
    <Card className="card-elevated" data-testid="admin-feedback-tab">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <MessageSquareHeart className="w-5 h-5 text-primary" /> Opiniones de usuarios
            </CardTitle>
            <CardDescription>
              Feedback enviado por colaboradores y promotores al finalizar su participación.
            </CardDescription>
          </div>
          {data && (
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <p className="font-semibold text-lg" data-testid="feedback-avg">{data.avg_rating ?? '—'}</p>
                <p className="text-[11px] text-muted-foreground">Media</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg" data-testid="feedback-count">{data.total_submitted}</p>
                <p className="text-[11px] text-muted-foreground">Recibidas</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg">{data.sent_pending}</p>
                <p className="text-[11px] text-muted-foreground">Sin responder</p>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="feedback-empty">
            Aún no se ha recibido ninguna opinión. Se solicitan automáticamente al colaborador al terminar su
            aportación y al promotor cuando recibe su libro.
          </p>
        ) : (
          <div className="space-y-3">
            {data.items.map((f) => (
              <div key={f.feedback_id} className="border rounded-lg p-3" data-testid={`feedback-item-${f.feedback_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Stars n={f.rating || 0} />
                    <span className="text-sm font-medium">{f.name || f.email || 'Anónimo'}</span>
                    <Badge className={f.source === 'promoter'
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px]'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]'}>
                      {f.source === 'promoter' ? 'Promotor' : 'Colaborador'}
                    </Badge>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {f.book_title ? `${f.book_title} · ` : ''}{(f.submitted_at || '').slice(0, 10)}
                  </span>
                </div>
                {f.comment && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{f.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
